import React, { useEffect, useMemo, useRef, useState } from 'react';

const REPORT_REASONS = [
  'Contenu inapproprié',
  'Spam ou publicité',
  "Droits d'auteur",
  'Violence ou haine',
  'Harcèlement',
  'Autre'
];

const resolveImageUrl = (backendUrl, url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const filename = url.split('/').pop();
  return `${backendUrl}/api/images/${filename}`;
};

const inferVideoType = (url = '') => {
  const clean = url.split('?')[0].toLowerCase();
  if (clean.endsWith('.mp4')) return 'video/mp4';
  if (clean.endsWith('.mov')) return 'video/quicktime';
  if (clean.endsWith('.webm') || clean.endsWith('.bin')) return 'video/webm';
  return undefined;
};

const getStoryFormat = (story) => {
  const wRaw = Number(story?.format_width);
  const hRaw = Number(story?.format_height);
  if (Number.isFinite(wRaw) && Number.isFinite(hRaw) && wRaw > 0 && hRaw > 0) {
    return { w: wRaw, h: hRaw };
  }
  const id = story?.format_id;
  if (id === 'mobile-9-16') return { w: 9, h: 16 };
  if (id === 'landscape-16-9') return { w: 16, h: 9 };
  if (id === 'square-1-1') return { w: 1, h: 1 };
  return null;
};

const StoryCard = React.memo(function StoryCard({
  story,
  index,
  backendUrl,
  currentUser,
  onOpenStory,
  onOpenReport,
  onOpenDelete
}) {
  const thumbnailSrc = story.thumbnail_url ? resolveImageUrl(backendUrl, story.thumbnail_url) : null;
  const videoType = inferVideoType(story.video_url || '');
  const videoRef = useRef(null);
  const storyFormat = useMemo(() => getStoryFormat(story), [story]);
  const fitClass = useMemo(() => {
    if (!storyFormat) return 'object-contain';
    return storyFormat.h >= storyFormat.w ? 'object-cover' : 'object-contain';
  }, [storyFormat]);
  const isOwner = !!(currentUser && Number(story.user_id) === Number(currentUser.id));

  useEffect(() => {
    if (thumbnailSrc) return;
    const video = videoRef.current;
    if (!video) return;

    const handleLoaded = () => {
      try {
        const stillTime = Math.min(0.01, Math.max((video.duration || 0.01) - 0.01, 0));
        video.currentTime = stillTime;
        video.pause();
      } catch (_) {}
    };

    video.addEventListener('loadedmetadata', handleLoaded);
    return () => video.removeEventListener('loadedmetadata', handleLoaded);
  }, [thumbnailSrc, story.video_url]);

  return (
    <div
      className="group relative w-44 sm:w-48 md:w-52 aspect-[9/16] flex-shrink-0 rounded-2xl overflow-hidden bg-white/5 border border-white/10 cursor-pointer"
      onClick={() => onOpenStory?.(index)}
    >
      {thumbnailSrc ? (
        <img
          src={thumbnailSrc}
          alt={`Story ${story.id}`}
          className={`absolute inset-0 w-full h-full ${fitClass} pointer-events-none`}
          loading="lazy"
        />
      ) : (
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full ${fitClass} pointer-events-none`}
          muted
          playsInline
          preload="metadata"
        >
          <source src={story.video_url} type={videoType} />
        </video>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none"></div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          if (isOwner) {
            onOpenDelete?.(story);
          } else {
            onOpenReport?.(story);
          }
        }}
        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        title={isOwner ? 'Supprimer' : 'Signaler'}
      >
        ⋯
      </button>

      <div className="absolute bottom-0 left-0 right-0 p-3 pointer-events-none">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full overflow-hidden bg-white/10 flex items-center justify-center text-xs">
            <img
              src={story.user_profile_image ? resolveImageUrl(backendUrl, story.user_profile_image) : '/default-avatar.svg'}
              alt={story.user_name || 'Utilisateur'}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="text-sm font-semibold truncate">{story.user_name || 'Utilisateur'}</div>
        </div>
      </div>
    </div>
  );
});

export default function StorySection({ stories = [], onCreateStory, onReportStory, onDeleteStory, isLoggedIn, onOpenStory, currentUser, onReachEnd }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const scrollRef = useRef(null);
  const [hasScrolledRight, setHasScrolledRight] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const hasRequestedAllRef = useRef(false);

  const handleScroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = Math.max(260, Math.floor(el.clientWidth * 0.85));
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
    if (direction === 'right') {
      setHasScrolledRight(true);
    }
  };

  const handleListScroll = () => {
    const el = scrollRef.current;
    if (!el || !onReachEnd || hasRequestedAllRef.current) return;
    const nearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 120;
    if (nearEnd) {
      hasRequestedAllRef.current = true;
      onReachEnd();
    }
  };

  const visibleStories = useMemo(() => stories || [], [stories]);

  return (
    <div className="mb-2">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">Story</h2>
          <p className="text-sm text-white/60">Partage des moments en format vidéo.</p>
        </div>
        <button
          onClick={onCreateStory}
          className="text-sm font-bold text-white px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all"
        >
          <span className="md:hidden">Créer</span>
          <span className="hidden md:inline">Créer une story</span>
        </button>
      </div>

      <div className="relative">
        {hasScrolledRight && (
          <button
            type="button"
            onClick={() => handleScroll('left')}
            aria-label="Défiler vers la gauche"
            className="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-black/60 hover:bg-black/80 border border-white/10"
          >
            ‹
          </button>
        )}

        <div
          ref={scrollRef}
          onScroll={handleListScroll}
          className="flex gap-4 overflow-x-auto scroll-smooth pb-2 pr-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {visibleStories.length === 0 ? (
            <div className="text-sm text-white/50 py-6 px-2">
              Aucune story pour le moment.
            </div>
          ) : (
            visibleStories.map((story, index) => (
              <StoryCard
                key={story.id}
                story={story}
                index={index}
                backendUrl={BACKEND_URL}
                currentUser={currentUser}
                onOpenStory={onOpenStory}
                onOpenReport={setReportTarget}
                onOpenDelete={setDeleteTarget}
              />
            ))
          )}
        </div>

        <button
          type="button"
          onClick={() => handleScroll('right')}
          aria-label="Défiler vers la droite"
          className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-black/60 hover:bg-black/80 border border-white/10"
        >
          ›
        </button>
      </div>

      {reportTarget && (
        <div className="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 modal-text">
          <div className="bg-[#14141f] border border-white/10 rounded-2xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Signaler une story</h3>
              <button
                onClick={() => setReportTarget(null)}
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-white/60 mb-4">
              Choisis un motif de signalement.
            </p>
            <div className="space-y-2">
              {REPORT_REASONS.map((reason) => (
                <button
                  key={reason}
                  onClick={() => {
                    onReportStory?.(reportTarget.id, reason);
                    setReportTarget(null);
                  }}
                  className="w-full text-left px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm"
                >
                  {reason}
                </button>
              ))}
            </div>
            {!isLoggedIn && (
              <div className="mt-3 text-xs text-red-300">
                Tu dois être connecté pour signaler une story.
              </div>
            )}
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 modal-text">
          <div className="bg-[#14141f] border border-white/10 rounded-2xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Supprimer la story</h3>
              <button
                onClick={() => setDeleteTarget(null)}
                className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-white/60 mb-4">
              Cette action est définitive. Veux-tu vraiment supprimer ta story ?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  onDeleteStory?.(deleteTarget.id);
                  setDeleteTarget(null);
                }}
                className="flex-1 px-4 py-2 rounded-xl bg-red-500/80 hover:bg-red-500 text-sm font-semibold"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
